/**
 * Header hide-on-scroll-down / reveal-on-scroll-up, scoped to pages with a
 * transparent header (`#header-component[transparent]`). Applies two
 * classes to <body>, which hero.liquid's stylesheet turns into the actual
 * visual treatment (transform/background on #header-component, opacity on
 * the docked wordmark from cm-hero-logo-shrink.js when present):
 *
 *   - Anywhere within the "pinned area" (scrollY 0 to its bottom edge): no
 *     class — the header keeps its default fixed/transparent styling the
 *     whole time.
 *   - Past the pinned area, scrolling down (past a 10px threshold): `cm-
 *     header-hidden` — header (+ docked wordmark, if any) slide/fade out
 *     of view.
 *   - Past the pinned area, scrolling up (past a 10px threshold): `cm-
 *     header-revealed` — header (+ docked wordmark) slide/fade back in,
 *     header background goes solid.
 *
 * The "pinned area" is the homepage's pinned-logo hero
 * (`.hero[data-cm-logo-shrink="true"]`) when present (e.g. the homepage),
 * or the sticky product-media column on PDP as a fallback — either way,
 * this only reads that element's bounds to find where it ends. It never
 * touches cm-hero-logo-shrink.js's pin/scrub math, and only toggles
 * opacity (never position/transform) on the docked wordmark.
 *
 * Uses the theme's own scroll-container helpers (the same ones header.js
 * and cm-hero-logo-shrink.js use) since Horizon's squeeze layout scrolls
 * `.page-wrapper` on desktop (≥990px), not `window`.
 */
import { getScrollTop, getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function init() {
  if (!document.querySelector('#header-component[transparent]')) return;

  var pinnedArea =
    document.querySelector('.hero[data-cm-logo-shrink="true"]') ||
    document.querySelector('.product-information__media');
  if (!pinnedArea) return;

  var THRESHOLD = 10;

  var heroBottom = 0;
  var announcementHeight = 0;
  var lastScrollY = 0;
  var direction = 'none';
  var state = 'in-hero';
  var ticking = false;
  var scrollTarget = null;
  var lastFixedTop = -1;

  function measure() {
    var container = getScrollContainer();
    var containerRect = container.getBoundingClientRect();
    var heroRect = pinnedArea.getBoundingClientRect();
    var heroTop = heroRect.top - containerRect.top + container.scrollTop;
    heroBottom = heroTop + pinnedArea.offsetHeight;

    var announcementBar = document.querySelector('.cm-announcement-bar');
    announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;

    lastFixedTop = -1;
    lastScrollY = getScrollTop();
    update();
  }

  function applyState(next) {
    if (next === state) return;
    state = next;
    document.body.classList.toggle('cm-header-hidden', state === 'hidden');
    document.body.classList.toggle('cm-header-revealed', state === 'revealed');
  }

  // Making the header `position: fixed` (needed so it can slide fully out
  // of view past the hero) loses the offset it naturally had from
  // `.header-section`'s own `position: relative` box, which sits below the
  // announcement bar in normal flow — without this, the fixed header
  // would sit at true viewport top and overlap the still-visible
  // announcement bar for the first `announcementHeight` px of scroll.
  function applyFixedTop(scrollY) {
    var top = Math.max(0, announcementHeight - scrollY);
    if (top === lastFixedTop) return;
    lastFixedTop = top;
    document.documentElement.style.setProperty('--cm-header-fixed-top', top + 'px');
  }

  function update() {
    ticking = false;

    var scrollY = getScrollTop();
    var delta = scrollY - lastScrollY;

    applyFixedTop(scrollY);

    if (scrollY <= heroBottom) {
      applyState('in-hero');
      lastScrollY = scrollY;
      return;
    }

    if (Math.abs(delta) >= THRESHOLD) {
      applyState(delta > 0 ? 'hidden' : 'revealed');
      lastScrollY = scrollY;
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function bindScrollTarget() {
    if (scrollTarget) {
      scrollTarget.removeEventListener('scroll', onScroll);
    }
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) {
      ticking = false;
      update();
    }
  });

  var resizeTimeout;
  function onResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(measure, 100);
  }

  bindScrollTarget();
  measure();
  window.addEventListener('resize', onResize);
  window.addEventListener('load', measure);

  scrollContainerMediaQuery.addEventListener('change', function () {
    bindScrollTarget();
    measure();
  });

  window.__cmHeaderScrollTestHook = update;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
